/*
 * お選びガイドの「定義モード」（コレクションのメタフィールド tuna.filter_def）の純粋関数。
 *
 * 定義に書いたとおりにだけ絞り込みを出すため、本体（collection-selection-guide.js）が
 * 値の取り込み・軸の並び・選択肢の並び・表示名をここに問い合わせる。
 * ここの関数は引数から戻り値を作るだけで、DOM・グローバル状態・閉包の状態を読まない。
 *
 * スキーマ v1（SPEC.md Part 2「定義の置き場と形」が正）:
 *   { v: 1, mode: 'step'|'flat',
 *     items: [{ key, label?, show: 'list'|'numeric', unit?,
 *               unlisted?: 'none'|'hide',                  // 未登録値の扱い（既定 none）
 *               values?: [{ v, label?, desc?, image? }],   // show=list で必須。並び＝表示順
 *                                                          // show=numeric では任意（表示名・説明・画像だけ。並びに使わない）
 *               values_order?: [刻み後の値キー],            // show=numeric で任意
 *               ranges?: ['lo-hi', ...] }] }                // show=numeric で任意。values_order との併記は不可
 *
 * ranges（区分）:
 *   "300-650" のような「整数-整数」（lo<=hi・両端を含む）の文字列。空配列・重複は不可、区分どうしの重なりは可。
 *   文字列そのものが選択肢の値キー兼表示順。どの区分にも入らない値は OTHER（'__other__'、表示名「その他」）に入り、
 *   並びは 区分 → その他 →（呼び出し側が付ける）指定なし。振り分けは本体が ranges(item) を見て行う。
 *
 * unlisted:
 *   none … 未登録値は行から捨てる（その商品は「指定なし」に数える）
 *   hide … 未登録値は選択肢に出さないが行からは捨てない（「指定なし」に数えない）。
 *          旧経路のページ専用の選択肢（data-sg-allowed-values）と同じ見え方。
 *          numeric では values_order か ranges があるときだけ効く（そこに無い値キーを出さない。
 *          ranges のときも「その他」は出す）
 *
 * ブラウザでは ES5 相当で書く（本体と同じく var・function のみ）。
 */
(function(){
  'use strict';

  // 焼き込みの「1商品がその軸に持つ値の組」の区切り（U+001F）。
  // 本体の VALUE_SEP・scripts/bake_facet_axes.py の VALUE_SEP と同じ文字。
  var VALUE_SEP = String.fromCharCode(0x1F);

  // 区分を持つ軸で、どの区分にも入らない値のキー（表示名「その他」）。値なしの '__none__'（指定なし）とは別。
  var OTHER = '__other__';

  var has = Object.prototype.hasOwnProperty;

  // key・値は利用者が書く文字列なので "__proto__" も来る。内部の索引は原型を持たない入れ物にする
  function dict(){ return Object.create(null); }
  // 戻り値（普通のオブジェクト）に "__proto__" を自分の項目として入れる
  function put(obj, k, val){
    if (k === '__proto__') Object.defineProperty(obj, k, { value: val, enumerable: true, writable: true, configurable: true });
    else obj[k] = val;
  }

  function isArray(x){ return Object.prototype.toString.call(x) === '[object Array]'; }
  function isObject(x){ return !!x && typeof x === 'object' && !isArray(x); }
  function isNonEmptyString(x){ return typeof x === 'string' && x !== ''; }
  function given(x){ return x !== undefined && x !== null; }

  function fail(reason){ return { ok: false, def: null, error: reason }; }

  // 定義の検査。理由は root の data-sg-def-error にそのまま載るので短い記号＋対象にする。
  function check(def){
    if (!isObject(def)) return 'not-object';
    if (def.v !== 1) return 'bad-version';
    if (def.mode !== 'step' && def.mode !== 'flat') return 'bad-mode';
    if (!isArray(def.items) || !def.items.length) return 'no-items';
    var keys = dict();
    for (var i = 0; i < def.items.length; i++){
      var it = def.items[i];
      if (!isObject(it) || !isNonEmptyString(it.key)) return 'bad-key:' + i;
      if (keys[it.key]) return 'dup-key:' + it.key;
      keys[it.key] = 1;
      if (it.show !== 'list' && it.show !== 'numeric') return 'bad-show:' + it.key;
      if (it.unlisted !== undefined && it.unlisted !== 'none' && it.unlisted !== 'hide') return 'bad-unlisted:' + it.key;
      if (it.show === 'list'){
        if (!isArray(it.values)) return 'no-values:' + it.key;
      } else if (given(it.values_order) && !isArray(it.values_order)){
        return 'bad-values-order:' + it.key;
      }
      if (given(it.ranges)){
        if (it.show !== 'numeric' || !parseRanges(it.ranges)) return 'bad-ranges:' + it.key;
        // 並びの正が2つになるので併記させない
        if (given(it.values_order)) return 'ranges-with-values-order:' + it.key;
      }
      // numeric の values は任意（表示名・説明・画像だけ）。書いてあれば list と同じ検査をする
      if (it.values !== undefined){
        var bad = checkValues(it);
        if (bad) return bad;
      }
    }
    return null;
  }
  function checkValues(it){
    if (!isArray(it.values)) return 'bad-value:' + it.key;
    var seen = dict();
    for (var j = 0; j < it.values.length; j++){
      var val = it.values[j];
      if (!isObject(val) || !isNonEmptyString(val.v)) return 'bad-value:' + it.key;
      if (seen[val.v]) return 'dup-value:' + it.key + ':' + val.v;
      seen[val.v] = 1;
    }
    return null;
  }

  // ranges を [{key, lo, hi}] に読む。書式違反・lo>hi・空配列・重複は null。
  var RANGE_RE = /^(\d+)-(\d+)$/;
  function parseRanges(list){
    if (!isArray(list) || !list.length) return null;
    var out = [], seen = dict();
    for (var i = 0; i < list.length; i++){
      var s = list[i];
      var m = typeof s === 'string' ? RANGE_RE.exec(s) : null;
      if (!m || seen[s]) return null;
      var lo = parseInt(m[1], 10), hi = parseInt(m[2], 10);
      if (lo > hi) return null;
      seen[s] = 1;
      out.push({ key: s, lo: lo, hi: hi });
    }
    return out;
  }

  // 定義の JSON 文字列を読む。例外は投げない。
  // 空・null は 'empty'（定義なし）。呼び出し側はこれを「レガシー動作」として扱う。
  function parse(jsonText){
    if (jsonText === null || jsonText === undefined) return fail('empty');
    var text = String(jsonText);
    if (/^\s*$/.test(text)) return fail('empty');
    var def;
    try { def = JSON.parse(text); } catch (e) { return fail('invalid-json'); }
    if (def === null) return fail('empty');
    var reason = check(def);
    if (reason) return fail(reason);
    return { ok: true, def: def, error: null };
  }

  // key -> item。原型を持たない入れ物（key "__proto__" でも壊れない）
  function keyIndex(def){
    var out = dict();
    var items = (def && def.items) || [];
    for (var i = 0; i < items.length; i++) out[items[i].key] = items[i];
    return out;
  }

  function hides(item){ return !!item && item.unlisted === 'hide'; }

  // 行から未登録値を捨てるときの登録値の集合。numeric・unlisted=hide は null（値を絞らない）。
  function valueSet(item){
    if (!item || item.show !== 'list' || hides(item)) return null;
    return setOf(item.values || [], 'v');
  }
  function setOf(list, field){
    var set = dict();
    for (var i = 0; i < list.length; i++) set[field ? list[i][field] : list[i]] = 1;
    return set;
  }

  // key ごとの「登録値の集合」（numeric・hide は true＝全部通す）。定義外の key は持たない。
  function acceptIndex(def){
    var out = dict();
    var items = (def && def.items) || [];
    for (var i = 0; i < items.length; i++) out[items[i].key] = valueSet(items[i]) || true;
    return out;
  }

  function accepts(acc, key, value){
    var set = acc[key];
    if (!set) return false;
    return set === true || set[value] === 1;
  }

  // 商品行・索引行の [[key, value], ...] から、定義外の key と list の未登録値を捨てる。
  function filterTagPairs(def, pairs){
    var acc = acceptIndex(def), out = [];
    for (var i = 0; i < (pairs || []).length; i++){
      var p = pairs[i];
      if (accepts(acc, p[0], p[1])) out.push([p[0], p[1]]);
    }
    return out;
  }

  // 焼き込みの axes（{key: {h: 値を持つ商品数, v: {値の組: 件数}}}）を定義で絞る。
  // 組から未登録値を除いて残りで組み直し（同じ組になれば件数を合算）、
  // 全部消えた組の件数は「値を持つ商品数」h から引く（その商品は「指定なし」へ回る）。
  // 定義外の key は落とす。入力は書き換えない。
  function filterDigestAxes(def, digestAxes){
    var acc = acceptIndex(def), out = {};
    var src = digestAxes || {};
    for (var key in src){
      if (!has.call(src, key) || !acc[key]) continue;
      var ent = src[key] || {}, vmap = ent.v || {};
      var h = ent.h || 0, nv = {}, sums = dict(), combos = [];
      for (var combo in vmap){
        if (!has.call(vmap, combo)) continue;
        var n = vmap[combo];
        var kept = [], parts = combo.split(VALUE_SEP);
        for (var i = 0; i < parts.length; i++){
          if (accepts(acc, key, parts[i])) kept.push(parts[i]);
        }
        if (!kept.length){ h -= n; continue; }
        var k2 = kept.join(VALUE_SEP);
        if (!(k2 in sums)){ sums[k2] = 0; combos.push(k2); }
        sums[k2] += n;
      }
      for (var c = 0; c < combos.length; c++) put(nv, combos[c], sums[combos[c]]);
      var copy = {};
      for (var f in ent) if (has.call(ent, f)) put(copy, f, ent[f]);
      copy.h = h;
      copy.v = nv;
      put(out, key, copy);
    }
    return out;
  }

  // 選択肢の並び。
  //   list:    values の順。未登録は捨てる。渡されていない値は足さない（unlisted=hide でも同じ）
  //   numeric: values_order にある値をその順で先に、残りは渡された順（呼び出し側の数値順）。
  //            unlisted=hide で values_order があれば、残りは出さない
  //   numeric で ranges あり: 区分の順 → 区分に無い値キー（unlisted=hide なら出さない）→ その他（hide でも出す）
  // 「指定なし」は呼び出し側が末尾に付ける（ここには渡さない）。
  function orderValues(item, values){
    var i;
    values = values || [];
    var present = setOf(values);
    var out = [], used = dict();
    function take(v){
      if (present[v] === 1 && used[v] !== 1){ out.push(v); used[v] = 1; }
    }
    if (item && item.show === 'list'){
      var vals = item.values || [];
      for (i = 0; i < vals.length; i++) take(vals[i].v);
      return out;
    }
    var rs = ranges(item);
    if (rs){
      for (i = 0; i < rs.length; i++) take(rs[i].key);
      if (!hides(item)){
        for (i = 0; i < values.length; i++) if (values[i] !== OTHER) take(values[i]);
      }
      take(OTHER);
      return out;
    }
    var hasOrder = !!item && isArray(item.values_order);
    var order = hasOrder ? item.values_order : [];
    for (i = 0; i < order.length; i++) take(order[i]);
    if (hasOrder && hides(item)) return out;
    for (i = 0; i < values.length; i++) take(values[i]);
    return out;
  }

  // numeric の区分を書いた順に [{key: 'lo-hi', lo, hi}] で返す（毎回作り直すので書き換えてよい）。
  // 区分が無い・list・検査を通らない書き方は null（本体は従来の刻みに落とす）。
  function ranges(item){
    if (!item || item.show !== 'numeric' || !given(item.ranges)) return null;
    return parseRanges(item.ranges);
  }

  // 選択肢の表示名・説明・画像の登録（list の values。numeric も values を書けばそこから）
  function findValue(item, v){
    if (!item || !isArray(item.values)) return null;
    for (var i = 0; i < item.values.length; i++){
      if (item.values[i] && item.values[i].v === v) return item.values[i];
    }
    return null;
  }

  function str(x){ return typeof x === 'string' ? x : ''; }

  // 選択肢の表示名。定義の label（空なら値そのまま）。共通の filter_value は見ない。
  function valueLabel(item, v){
    var ent = findValue(item, v);
    return (ent && isNonEmptyString(ent.label)) ? ent.label : v;
  }

  // 選択肢の表示名・説明・画像。values に無い値は null（numeric で values を書いていなければ常に null）。
  function valueMeta(item, v){
    var ent = findValue(item, v);
    if (!ent) return null;
    return { label: valueLabel(item, v), desc: str(ent.desc), image: str(ent.image) };
  }

  // 軸の一覧（items の順）。見出しは label（無ければ key）。
  function axisList(def){
    var items = (def && def.items) || [], out = [];
    for (var i = 0; i < items.length; i++){
      var it = items[i];
      out.push({
        key: it.key,
        label: isNonEmptyString(it.label) ? it.label : it.key,
        show: it.show,
        unit: str(it.unit)
      });
    }
    return out;
  }

  // 形式。ハンドルや軸数からは決めない。
  function mode(def){
    return def && def.mode === 'step' ? 'step' : 'flat';
  }

  var MJGuideDef = {
    VALUE_SEP: VALUE_SEP,
    OTHER: OTHER,
    parse: parse,
    keyIndex: keyIndex,
    filterTagPairs: filterTagPairs,
    filterDigestAxes: filterDigestAxes,
    orderValues: orderValues,
    ranges: ranges,
    valueLabel: valueLabel,
    valueMeta: valueMeta,
    axisList: axisList,
    mode: mode
  };

  if (typeof window !== 'undefined') window.MJGuideDef = MJGuideDef;
  if (typeof module !== 'undefined') module.exports = MJGuideDef;
})();
